import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";

import {
  closeCrmChatDrawer,
  openCrmChatDrawer,
  CRM_CHAT_DRAWER_CLOSE_EVENT,
  CRM_CHAT_DRAWER_OPEN_EVENT,
} from "./CrmChatController";
import { useCrmChatLayoutInset } from "./CrmChatLayoutContext";
import { useAskPageContext } from "./hooks/useAskPageContext";
import { useChatAgentLoopFlag } from "./hooks/useChatAgentLoopFlag";
import { isEditableKeyboardTarget } from "./utils/askPageContext";
import { openOptionsFromPageContext } from "./utils/openAskFromPageContext";

/**
 * Floating Ask Type anything capsule + ⌘K (stories 12.1 / 12.5).
 * Hidden when agent-loop entitlement is off or the Ask drawer is open.
 */
export default function AskAnythingBar(): React.ReactElement | null {
  const { t } = useTranslation();
  const entitled = useChatAgentLoopFlag();
  const inset = useCrmChatLayoutInset();
  const pageContext = useAskPageContext();
  const { projectId = "" } = useParams<{ projectId?: string }>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setDrawerOpen(true);
    const onClose = () => setDrawerOpen(false);
    window.addEventListener(CRM_CHAT_DRAWER_OPEN_EVENT, onOpen);
    window.addEventListener(CRM_CHAT_DRAWER_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(CRM_CHAT_DRAWER_OPEN_EVENT, onOpen);
      window.removeEventListener(CRM_CHAT_DRAWER_CLOSE_EVENT, onClose);
    };
  }, []);

  useEffect(() => {
    if (!entitled || !projectId) return;

    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      if (isEditableKeyboardTarget(e.target)) return;
      e.preventDefault();
      if (drawerOpen) {
        closeCrmChatDrawer();
        return;
      }
      openCrmChatDrawer(openOptionsFromPageContext(projectId, pageContext));
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, entitled, pageContext, projectId]);

  if (!entitled || drawerOpen || !projectId) return null;

  const isMac =
    typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const shortcut = isMac ? "⌘K" : "Ctrl+K";

  return (
    <button
      type="button"
      data-testid="ask-anything-bar"
      aria-label={t("Ask Type anything") as string}
      onClick={() => openCrmChatDrawer(openOptionsFromPageContext(projectId, pageContext))}
      className="pointer-events-auto fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-lg transition hover:border-gray-300 hover:shadow-xl"
      style={{ marginRight: inset > 0 ? inset / 2 : undefined }}
    >
      <Sparkles className="h-4 w-4 text-blue-600" aria-hidden="true" />
      <span>{t("Ask Type anything")}</span>
      <kbd className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-normal text-gray-500">
        {shortcut}
      </kbd>
    </button>
  );
}
